import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createClient, getClient, updateClient } from '../services/clientsService';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';

const EMPTY = {
  tipoDocumento: 'RUC',
  nroDocumento: '',
  nombre: '',
  razonSocial: '',
  direccion: '',
  ubigueo: '',
  telefono: '',
  email: '',
};

export default function ClientFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const c = await getClient(id);
      setForm({
        tipoDocumento: c.tipoDocumento || 'RUC',
        nroDocumento: c.nroDocumento || '',
        nombre: c.nombre || '',
        razonSocial: c.razonSocial || c.nombreComercial || '',
        direccion: c.direccion || '',
        ubigueo: c.ubigueo || '',
        telefono: c.telefono || '',
        email: c.email || '',
      });
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateClient(id, { ruc, data: form });
        navigate(`/admin/clientes/${id}`);
      } else {
        const ref = await createClient({ ruc, data: form });
        navigate(`/admin/clientes/${ref.id}`);
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label="Cargando cliente…" />;

  return (
    <div className="page narrow">
      <header className="page-header">
        <div>
          <h1>{isEdit ? 'Editar cliente' : 'Nuevo cliente'}</h1>
          <p className="muted">Modelo real de `clientes` · RUC o DNI</p>
        </div>
      </header>

      <form className="form-card" onSubmit={onSubmit}>
        <div className="form-grid">
          <label>
            Tipo de documento *
            <select value={form.tipoDocumento} onChange={(e) => set('tipoDocumento', e.target.value)} required>
              <option value="RUC">RUC</option>
              <option value="DNI">DNI</option>
            </select>
          </label>
          <label>
            Número de documento *
            <input
              value={form.nroDocumento}
              onChange={(e) => set('nroDocumento', e.target.value)}
              required
              inputMode="numeric"
            />
          </label>
          {form.tipoDocumento === 'DNI' ? (
            <label className="span-2">
              Nombre completo *
              <input value={form.nombre} onChange={(e) => set('nombre', e.target.value)} required />
            </label>
          ) : (
            <label className="span-2">
              Razón social *
              <input value={form.razonSocial} onChange={(e) => set('razonSocial', e.target.value)} required />
            </label>
          )}
          <label className="span-2">
            Dirección
            <input value={form.direccion} onChange={(e) => set('direccion', e.target.value)} />
          </label>
          <label>
            Ubigueo
            <input value={form.ubigueo} onChange={(e) => set('ubigueo', e.target.value)} />
          </label>
          <label>
            Teléfono
            <input value={form.telefono} onChange={(e) => set('telefono', e.target.value)} />
          </label>
          <label>
            Email
            <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </label>
        </div>

        {error ? <div className="alert error">{error}</div> : null}

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar cliente'}
          </button>
        </div>
      </form>
    </div>
  );
}
